export interface ExperienceItem {
  title: string
  company: string
  period: string
  description: string
  technologies: string[]
  achievements: string[]
  website?: string

}

export interface ExperienceData {
  title: string
  subtitle: string
  description: string
  experiences: ExperienceItem[]
}

export const experienceData: ExperienceData = {
  title: "Work",
  subtitle: "Experience",
  description: "My professional journey in software development and DevOps engineering, showcasing my growth and expertise across various technologies and industries.",
  experiences: [
    {
      title: "Senior Software Engineer",
      company: "Flowframe.io",
      website: "https://app.flowframe.io",
      period: "Mar 2024 - Present",
      description: "At Flowframe.io, I transitioned into a senior engineering role where I combined software craftsmanship with modern DevOps practices to build resilient, production-ready systems.",
      technologies: ["React", "Next.js", "Node.js", "Python", "Flask", "Django", "TypeScript", "PostgreSQL", "Digital Ocean", "Docker", "MongoDB", "Websocket"],
      achievements: [
        "Developed interactive analytics dashboards using Next.js and Django REST APIs, improving user engagement and decision-making experience by 30%.",
        "Conducted regular code reviews focused on secure coding practices, reducing vulnerabilities and improving team code quality.",
        "Integrated DevSecOps practices into CI/CD by automating vulnerability scans and managing secrets securely, ensuring robust production hygiene.",
        "Oversaw the end-to-end lifecycle of full-stack features—from React/Next.js frontends to Django REST backends—within a cloud-native, automated environment."
      ]
    },
    {
      title: "AI Training Specialist (LLM/CUA) (Contract)",
      company: "Turing",
      period: "Dec 2025 - Present (Remote)",
      description: "Designed and validated high-fidelity training demonstrations for Computer Use Agents (CUA), focusing on technically correct, reproducible workflows across operating systems and real-world software engineering tasks.",
      technologies: ["LLMs", "Computer Use Agents (CUA)", "Prompt Engineering", "Technical Writing", "Linux", "macOS", "Windows", "CLI", "Debugging"],
      achievements: [
        "Designed high-fidelity demonstrations for Computer Use Agents (CUA), architecting step-by-step reasoning chains for complex workflows including environment configuration, data analysis pipelines, and full-stack debugging.",
        "Simulated multi-OS user tasks (Linux/macOS/Windows) to evaluate agent tool usage, identifying critical failure modes in command-line execution and file system management to improve model safety and accuracy.",
        "Collaborated with AI Research teams to refine context-aware prompts and activity logs, ensuring reproducible and technically correct model behaviors across diverse software ecosystems.",
        "Interpreted complex model errors to guide data-driven improvements in agent reasoning, with emphasis on secure operations and software engineering best practices."
      ]
    },
    {
      title: "Senior Backend Systems Engineer (Contract)",
      company: "PuzzlePieceSolutions",
      website: "https://puzzlepiecesolutions.com",
      period: "Sep 2024 - Dec 2025",
      description: "Led full-stack development of enterprise-grade web applications using Node.js/Express backend with MongoDB, implementing comprehensive DevOps practices and modern development workflows.",
      technologies: ["JavaScript", "Node.js", "Express", "MongoDB", "Git", "TypeScript", "Docker", "AWS", "CI/CD", "Agile", "Scrum"],
      achievements: [
        "Built comprehensive scheduling platform with 100+ API endpoints covering user management, booking systems, analytics, payment processing, and admin controls with JWT authentication, rate limiting, and IP blocking middleware",
        "Developed 17+ analytics endpoints providing real-time business intelligence including revenue tracking, client retention analysis, service performance metrics, and re-engagement strategies with time-based aggregations and MongoDB optimization",
        "Implemented complete DevOps pipeline with Docker containerization, GitHub Actions CI/CD, environment-specific configurations, automated testing, health checks, and monitoring for reliable deployments",
        "Integrated Stripe payment processing with webhook handling, subscription management, credit-based purchasing system, B2B lead generation, secure file uploads with Cloudinary, and real-time Socket.IO communication"
        ,"Created enterprise-grade email automation system with templated notifications, marketing campaigns, scheduled reminders, and multi-channel delivery supporting onboarding, booking confirmations, and customer re-engagement workflows"
      ]
    },
    {
      title: "Senior Software Developer",
      company: "Workgital",
      website:"https://workgital.com",
      period: "Feb 2023 - Jan 2025",
      description: "Led full-stack development using MERN stack architecture, mentoring junior developers and delivering end-to-end product solutions with microservices approach.",
      technologies: ["React", "JavaScript", "Node.js", "MongoDB", "Express", "HTML", "CSS", "Git", "GitHub"],
      achievements: [
        "Spearheaded the development of advanced application features using MERN stack and microservices architecture.",
        "Mentored 3+ junior developers in advanced JavaScript and system design, improving team productivity and code quality.",
        "Integrated multiple third-party APIs, expanding platform capabilities and enhancing user experience..",
        "Refactored backend logic in Node.js + MongoDB to achieve 20% faster data processing and reduced server load.",
        "Delivered full-cycle product development, from ideation to deployment, ensuring on-time delivery and stakeholder satisfaction."

      ]
    },
    {
      title: "DevOps / Backend Engineer",
      company: "Strides Forge",
      period: "Nov 2020 - Sep 2022",
      description: "This role marked my shift from pure software development into DevOps, where I learned to blend backend engineering with automation and infrastructure management",
      technologies: ["Python", "TypeScript", "Docker", "AWS", "CI/CD", "Agile", "Scrum"],
      achievements: [
        "Built and deployed containerized applications using Docker and managed versioned images with AWS ECR.",
        "Automated CI/CD pipelines to deploy features faster and reduce release times significantly.", 
        "Led a small team to deliver Python and TypeScript-based features while optimizing service deployment workflows.",
        "Collaborated on Node.js and Django APIs, improving system architecture and backend responsiveness.",
        "Implemented dashboards and alerting for system health, increasing visibility and reducing downtime."

      ]
    },
    {
      title: "Software Developer Intern",
      company: "Vlad Tech",
      period: "Dec 2019 - Aug 2020",
      description: "Joined as a software development intern in a fast-paced startup, gaining hands-on experience building web applications and working within agile teams.",
      technologies: ["JavaScript", "MongoDB", "REST APIs", "Git", "Agile", "Scrum"],
      achievements: [
        "Developed and integrated RESTful APIs using JavaScript and MongoDB to support data-driven features",
        "Contributed to backend functionality and logic enhancements, ensuring reliable and efficient application performance.", 
        "Participated in agile sprints, collaborating closely with developers, designers, and QA to ship features on time.",
        "Conducted code reviews and implemented feedback to maintain high code quality and development standards.",
        "Assisted in optimizing frontend performance and contributed to UI improvements using modern JavaScript frameworks."

      ]
    },
  ]
}